import { useEffect, useMemo, useState } from "react";
import { X, Share2, Loader2, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Create a multi-property collection from the Properties page.
 * Flow: pick lead → pick 1..N properties (min 2 encouraged) → name + note → mint.
 * On success: shows the share URL with one-click copy.
 */
export default function NewCollectionModal({ properties, defaultSelected = [], onClose, onCreated }) {
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState("");
  const [selected, setSelected] = useState(new Set(defaultSelected));
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { shareUrl }
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/leads").then(r => setLeads(r.data)).catch(() => {});
  }, []);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedProperties = useMemo(
    () => properties.filter(p => selected.has(p.id)),
    [properties, selected]
  );

  const suggestedName = useMemo(() => {
    if (name) return name;
    if (selectedProperties.length === 0) return "";
    const city = selectedProperties[0].city;
    return city ? `${city} picks` : `${selectedProperties.length} homes for you`;
  }, [name, selectedProperties]);

  const submit = async () => {
    if (!leadId) return toast.error("Pick a lead first");
    if (selected.size === 0) return toast.error("Pick at least one property");
    setBusy(true);
    try {
      const r = await api.post("/collections", {
        lead_id: leadId,
        name: (name || suggestedName).slice(0, 255),
        note: note || null,
        property_ids: Array.from(selected),
      });
      const shareUrl = `${window.location.origin}/share/${r.data.share_token}`;
      setResult({ shareUrl });
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(result.shareUrl);
    setCopied(true);
    toast.success("Copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center px-4 py-8 overflow-y-auto" role="dialog" aria-label="New collection" data-testid="new-collection-modal">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between px-6 pt-6 pb-3">
          <div className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-slate-500" />
            <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900">New collection</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-md text-slate-400 hover:bg-slate-100" aria-label="Close" data-testid="collection-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {!result ? (
          <div className="px-6 pb-6 space-y-4">
            <label className="block">
              <span className="block text-xs font-medium uppercase tracking-widest text-slate-500 mb-1.5">Send to</span>
              <select value={leadId} onChange={e => setLeadId(e.target.value)} className="input" data-testid="collection-lead">
                <option value="">Pick a lead…</option>
                {leads.map(l => <option key={l.id} value={l.id}>{l.full_name} · {l.stage}</option>)}
              </select>
            </label>

            <div>
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-xs font-medium uppercase tracking-widest text-slate-500">Properties</span>
                <span className="text-xs text-slate-500">{selected.size} selected · {selected.size >= 3 ? "great tour size" : "add more for a real tour"}</span>
              </div>
              {properties.length === 0 ? (
                <div className="text-sm text-slate-500 rounded-md border border-dashed border-slate-300 p-4 text-center">No properties yet — add a few first.</div>
              ) : (
                <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                  {properties.map(p => {
                    const on = selected.has(p.id);
                    return (
                      <label key={p.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${on ? "bg-slate-50" : "hover:bg-slate-50"}`} data-testid={`pick-${p.id}`}>
                        <input type="checkbox" checked={on} onChange={() => toggle(p.id)} className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20" />
                        {p.hero_image_url ? (
                          <img src={p.hero_image_url} alt="" className="h-10 w-14 rounded object-cover shrink-0" />
                        ) : (
                          <div className="h-10 w-14 rounded bg-slate-100 shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-slate-900 truncate">{p.address_line1}</div>
                          <div className="text-xs text-slate-500 truncate">{[p.city, p.state].filter(Boolean).join(", ")} · {p.list_price ? `$${Number(p.list_price).toLocaleString()}` : "—"}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <label className="block">
              <span className="block text-xs font-medium uppercase tracking-widest text-slate-500 mb-1.5">Collection name</span>
              <input value={name} onChange={e => setName(e.target.value)} placeholder={suggestedName || "e.g. Gilbert homes under 650"} className="input" data-testid="collection-name" />
            </label>

            <label className="block">
              <span className="block text-xs font-medium uppercase tracking-widest text-slate-500 mb-1.5">Personal note <span className="text-slate-400 normal-case font-normal">(optional)</span></span>
              <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Three from our chat — the first is my favorite." className="input min-h-[60px]" data-testid="collection-note" />
            </label>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={submit} disabled={busy || !leadId || selected.size === 0} className="inline-flex items-center gap-1.5 rounded-md px-5 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--primary-hex)" }} data-testid="collection-submit">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />} Mint share link
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="rounded-md border-l-4 border-emerald-400 bg-emerald-50 p-3 text-sm text-emerald-900">
              Collection ready. Share the link — every reaction feeds this lead's score in real time.
            </div>
            <div className="flex items-stretch gap-2">
              <input readOnly value={result.shareUrl} className="input flex-1 font-mono text-xs" data-testid="collection-share-url" />
              <button onClick={copy} className="inline-flex items-center gap-1 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-3 text-sm font-semibold" data-testid="collection-copy-btn">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex items-center justify-end">
              <button onClick={onClose} className="text-sm font-semibold text-slate-600 hover:text-slate-900">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
